import {TabWrapper} from "../TabWrapper";
import {CircularProgress, Tab, Tabs} from "@mui/material";
import React, {useEffect} from "react";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {api} from "../../../../api/API";
import {useProjectId} from "../ProjectName";
import {usePricingKit2} from "./usePricingKit2";
import {EventEmitter, useAsync2} from "nate-react-api-helpers";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {PriceCategory, ZeroItem} from "../../../../api/Pricing";
import {DefaultTabControls} from "../../../../misc/userpref/useDefaultTab";

export const pricingUpdatedEmitter = new EventEmitter<boolean>();

export function PricingTabs(props: {
    tab: string;
    onTabChange(value: string): void;
    defaultTab?: DefaultTabControls;
    filterByChangeset?: boolean;
    hideSummary?: boolean;
    refunds?: boolean;
    sov?: number;
}) {
    const project = useProjectId();
    const kit = usePricingKit2(project);
    const kitLoading = !!kit.Loading;

    const needsPriceCounts = useAsync2((input) => {
        if(input.kitLoading) return Promise.resolve([] as ZeroItem[]);

        return api.pricing.getZerosByCategory({
            project: input.project,
            previewChangeset: input.sessionId,
            filterByChangeset: input.filterByChangeset,
            refunds: input.refunds,
            sov: input.sov,
        });
    }, {
        kitLoading: kitLoading,
        sessionId: kit.isPreview ? kit.sessionId : undefined,
        filterByChangeset: kit.isPreview ? true : props.filterByChangeset,
        project: project,
        refunds: props.refunds,
        sov: props.sov,
    }, [kitLoading, kit.sessionId, kit.isPreview, props.filterByChangeset, project, props.refunds, props.sov]);

    const reloadRef = useSyncedRef(needsPriceCounts.reload)

    useEffect(() => {
        const sub = pricingUpdatedEmitter.subscribe(() => {
            reloadRef.current();
        })

        return () => sub.cancel();
    }, [reloadRef]);

    const changeTabRef = useSyncedRef(props.onTabChange)
    const dt = props.defaultTab;
    const tabProps = (value: string) => dt ? dt.tabProps(value) : {};
    const tab = props.tab;
    const filterByChangeset = props.filterByChangeset;
    const hideSummary = props.hideSummary || filterByChangeset;
    useEffect(() => {
        if(tab === "summary" && hideSummary) {
            changeTabRef.current("frame-hm");
        }
    }, [changeTabRef, tab, hideSummary]);

    return (
        <>
            <TabWrapper>
                <Tabs className={tabGroup} value={props.tab} onChange={(e, value) => {
                    props.onTabChange(value)
                }}>
                    {hideSummary ? null : <Tab label="Summary" value="summary" />}
                    <Tab label={makeCount("HM Frame", "frame-hm", needsPriceCounts.result)} value="frame-hm" {...tabProps("frame-hm")} />
                    <Tab label={makeCount("Anchors", "frame-anchor", needsPriceCounts.result)} value="frame-anchor" {...tabProps("frame-anchor")} />
                    <Tab label={makeCount("HM Door", "door-hm", needsPriceCounts.result)} value="door-hm" {...tabProps("door-hm")} />
                    <Tab label={makeCount("WD Door", "door-wd", needsPriceCounts.result)} value="door-wd" {...tabProps("door-wd")} />
                    <Tab label={makeCount("Hardware", "hardware", needsPriceCounts.result)} value="hardware" {...tabProps("hardware")} />
                    <Tab label={makeCount("Glazing", "glazing", needsPriceCounts.result)} value="glazing" {...tabProps("glazing")} />
                    <Tab label={makeCount("Div 10", "div10", needsPriceCounts.result)} value="div10" {...tabProps("div10")} />
                    <Tab label={makeCount("Riders", "rider", needsPriceCounts.result)} value="rider" {...tabProps("rider")} />
                    <Tab label={makeCount("Misc", "misc", needsPriceCounts.result)} value="misc" {...tabProps("misc")} />

                    {needsPriceCounts.loading && <CircularProgress size={15} />}
                    {needsPriceCounts.error ? needsPriceCounts.LoadingOrErrorElement : null}
                </Tabs>
                {dt?.controls}
            </TabWrapper>
        </>
    )
}

function makeCount(name: string, key: PriceCategory | "rider" | "misc", list: ZeroItem[] | null) {
    if(!list) return name;

    const item = list.find(l => l.category === key);
    if(!item) return name;

    return name + " (" + item.count + ")";
}

export async function onPricingChangeWrap<T>(result: Promise<T>): Promise<T> {
    const r = await result;
    pricingUpdatedEmitter.emit(true);
    return r;
}